import ComponentTitle from '@/pages/safety-equipment/widgets/component-title'
import { Button, Modal, Table, Tooltip } from 'antd'
import React, { useEffect, useState } from 'react'
import styled from 'styled-components'
import type { ColumnsType } from 'antd/es/table'
import { formatDateTime } from '@/utils/date'
import { t } from '@/i18n'
import { useGetEnvManagementList, useStartStopEnvManagement } from './utils'
import { Message } from '@/components'

interface PropsType {
  open: boolean
  agentId: string
  clientStatus: number
  onClose: () => void
}

interface EnvData {
  dependencyId: string
  name: string
  connectionStatus: 'ConnectionStatusOnline' | 'ConnectionStatusOffline' | 'ConnectionStatusStopped'
  updateTime: string
}

const EnvManagement: React.FC<PropsType> = ({ open, onClose, agentId, clientStatus }) => {
  console.log(clientStatus)
  const [envManagementList, setEnvManagementList] = useState<EnvData[]>([])
  // 获取验证环境管理列表
  const [getEnvManagementList, loading] = useGetEnvManagementList()
  // 启停客户端验证环境
  const [startStopEnvManagement, startStopEnvManagementLoading] = useStartStopEnvManagement()

  const columns: ColumnsType<EnvData> = [
    {
      title: t('依赖名称'),
      dataIndex: 'name',
      key: 'name',
      width: '25%',
    },
    {
      title: t('在线状态'),
      dataIndex: 'connectionStatus',
      key: 'connectionStatus',
      width: '25%',
      render: (connectionStatus: string) => {
        const statusConfig = {
          ConnectionStatusOnline: { color: '#67C23A', text: t('在线') },
          ConnectionStatusOffline: { color: '#677280', text: t('离线') },
          ConnectionStatusStopped: { color: '#F53F3F', text: t('停止') },
        }
        const dotStyle = {
          display: 'inline-block',
          width: '6px',
          height: '6px',
          borderRadius: '50%',
          marginRight: '8px',
          backgroundColor: statusConfig[connectionStatus]?.color,
        }
        return (
          <span>
            <span style={dotStyle} />
            {statusConfig[connectionStatus]?.text}
          </span>
        )
      },
    },
    {
      title: t('最近更新时间'),
      dataIndex: 'updateTime',
      key: 'updateTime',
      width: '25%',
      render: text => (
        <Tooltip title={formatDateTime(text, 'standard')}>{formatDateTime(text, 'mdhm')}</Tooltip>
      ),
    },
    {
      title: t('操作管理'),
      key: 'action',
      width: '25%',
      render: (_, record) => (
        <Button
          type="link"
          loading={startStopEnvManagementLoading}
          style={{
            color: clientStatus === 2 ? '#999999' : '#4238C9',
            padding: 0,
            cursor: clientStatus === 2 ? 'not-allowed' : 'pointer',
          }}
          onClick={() => handleAction(record)}
          disabled={clientStatus === 2}
        >
          {record.connectionStatus === 'ConnectionStatusOffline' ? t('启动') : t('停止运行')}
        </Button>
      ),
    },
  ]

  const fetchEnvManagementList = async () => {
    try {
      const res = await getEnvManagementList(agentId)
      if (!res || !res.data) return

      setEnvManagementList(res.data.dependencyEnvs)
    } catch (error) {}
  }

  const handleAction = async (record: EnvData) => {
    try {
      const res = await startStopEnvManagement(
        agentId,
        record.dependencyId,
        record.connectionStatus === 'ConnectionStatusOffline' ? true : false,
      )

      if (!res || !res.data) return

      Message.success(t('操作成功'))
      fetchEnvManagementList()
    } catch (error) {}
  }

  useEffect(() => {
    if (open && agentId) {
      fetchEnvManagementList()
    }
  }, [open])

  return (
    <Wrapper
      width={800}
      title={<ComponentTitle title={t('验证环境管理')} />}
      open={open}
      onCancel={onClose}
      centered
      footer={null}
    >
      <StyledTable
        loading={loading}
        columns={columns}
        dataSource={envManagementList}
        pagination={false}
        rowKey="dependencyId"
      />
    </Wrapper>
  )
}

export default EnvManagement

const Wrapper = styled(Modal)`
  .ant-modal-content {
    border-radius: 8px;
    position: relative;

    .ant-modal-close {
      width: 36px;
      height: 36px;
      background: #d8d6f4;
      box-shadow: -1px 4px 10px 0px #c7c7c7;
      border-radius: 6px;
      border: none;
      color: #4238c9;
      font-size: 16px;
      font-weight: 500;
      position: absolute;
      right: -8px;
      top: -8px;
      transition: transform 0.3s ease;

      &:hover {
        transform: translate(-18px, 18px);
        box-shadow: 0 0 0 0;
      }

      .ant-modal-close-x {
        width: 100%;
        height: 100%;
        line-height: 36px;
        text-align: center;
      }
    }

    .ant-modal-header {
      height: 56px;
      background: #f2f0ff;
      border-bottom: none;
      margin: 0;
      padding: 19px 20px;
    }

    .ant-modal-body {
      height: 512px;
      padding: 24px;
      overflow-y: auto;
    }

    .ant-modal-footer {
      border-top: none;
      padding: 0 24px 16px;

      .ant-btn {
        height: 32px;
        padding: 0 16px;
        border-radius: 6px;

        &.cancel-btn {
          color: #666;
          border-color: #999;
        }

        &.ant-btn-primary {
          background: linear-gradient(270deg, #4238c9 100%, #4238c9 100%);
          border-color: #4238c9;

          &:hover {
            background: linear-gradient(270deg, #4238c9 0%, #6c65d4 100%);
            border-color: #6c65d4;
          }
        }
      }
    }
  }
`

const StyledTable = styled(Table)`
  .ant-table {
    background: transparent;
  }

  .ant-table-thead > tr > th {
    background: #f8f9fb;
    color: #86909c;
    font-weight: normal;
    border-bottom: none;
    padding: 12px 16px;

    &::before {
      display: none;
    }
  }

  .ant-table-tbody > tr > td {
    border-bottom: 1px solid #e5e6eb;
    padding: 12px 16px;
  }

  .ant-table-tbody > tr:last-child > td {
    border-bottom: none;
  }
`
